/**
 * AdminApplicantsPage — one gated role, filterable by approval state.
 *
 * Serves the Contractors and Verifiers entries under Users, plus the top-level
 * Applications queue: the `group` prop picks the role and columns, ?approval=
 * filters the state (all | approved | pending | rejected), and the queue adds
 * a ?role= filter across every gated role.
 *
 * Reads GET /api/admin/applications (adminApplicationsService.list), which
 * filters gated roles by u.approval_status. Pagination and search live in the
 * URL, matching AdminUsersPage.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  Skeleton,
  Alert,
  Button,
  Link,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

import adminApplicationsService from '../services/adminApplicationsService';
import AssignmentCount from '../components/admin/AssignmentCount';
import RoleChip from '../components/admin/RoleChip';
import { panelSx, tableSx, tableRowSx } from '../components/admin/surfaces';
import ApplicationsToolbar from '../components/admin/ApplicationsToolbar';
import { brand, status as statusTokens, text, bg, border } from '../theme/colors';

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

const EMPTY = '—';

// The approval states the applications endpoint accepts, as a page filter.
const APPROVAL_STATES = {
  approved: { label: 'Approved', tone: statusTokens.successDark, bg: statusTokens.successBg },
  pending: { label: 'Pending', tone: statusTokens.warningDark, bg: statusTokens.warningBg },
  rejected: { label: 'Rejected', tone: statusTokens.errorDark, bg: statusTokens.errorBg },
  // Still pending, but set aside after four unanswered document reminders;
  // any upload puts them back in Pending.
  archived: { label: 'Archived', tone: statusTokens.neutral, bg: statusTokens.neutralBg },
};
const APPROVAL_KEYS = Object.keys(APPROVAL_STATES);

// GATED_ROLES on the backend, minus professional_verifier (no live rows).
const APPLICANT_ROLE_OPTIONS = ['contractor', 'verifier', 'quality_inspector', 'merchant', 'realtor', 'conveyancer'];

const formatDate = (value) => {
  if (!value) return EMPTY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const initialsOf = (row) => {
  const first = (row.firstName || '').trim();
  const last = (row.lastName || '').trim();
  if (first || last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return (row.email || '?').charAt(0).toUpperCase();
};

const fullNameOf = (row) => `${row.firstName || ''} ${row.lastName || ''}`.trim();
const locationOf = (row) => [row.city, row.province].filter(Boolean).join(', ') || EMPTY;

// Columns shared by both groups, with the group-specific one spliced in.
const EXPERIENCE_COLUMN = {
  key: 'experience',
  label: 'Experience',
  render: (row) => (row.yearsExperience != null ? `${row.yearsExperience} yr` : EMPTY),
};

// Verifiers are scoped to what they may verify. The stored values are
// VERIFIER_TYPES ('client' | 'contractor') and stay as-is; only the display
// label changes — a 'client' verifier checks work on site.
const VERIFIER_TYPE_LABELS = { client: 'Work', contractor: 'Contractor' };

const VERIFIES_COLUMN = {
  key: 'verifies',
  label: 'Verifies',
  render: (row) =>
    row.verifierTypes?.length ? (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {row.verifierTypes.map((t) => (
          <Chip
            key={t}
            size="small"
            label={VERIFIER_TYPE_LABELS[t] || t.replace(/_/g, ' ')}
            sx={{
              fontSize: 11,
              height: 22,
              bgcolor: bg.subtle,
              color: text.secondary,
            }}
          />
        ))}
      </Box>
    ) : (
      EMPTY
    ),
};

// Verification workload, from AdminDashboardService._verificationStatsFor.
// Only approved verifiers hold assignments in practice, so pending/rejected
// rows simply read as empty.
const VERIFICATIONS_COLUMN = {
  key: 'verifications',
  label: 'Verifications',
  render: (row) => (
    <AssignmentCount
      stats={row.verificationStats}
      sourceLabels={{
        projectVerifications: 'project',
        propertyVerifications: 'property',
        contractorVetting: 'contractor vetting',
      }}
    />
  ),
};

const VIEW_COLUMN = {
  key: 'view',
  label: '',
};

// Without this, an unfiltered list gives no per-row signal — the header chip
// only describes the filter, so "All" showed nothing at all.
const APPROVAL_COLUMN = {
  key: 'approval',
  label: 'Approval',
  render: (row) => {
    const state = APPROVAL_STATES[row.approvalStatus];
    return (
      <Chip
        size="small"
        label={state?.label || String(row.approvalStatus || '').replace(/_/g, ' ') || 'Unknown'}
        sx={{
          textTransform: 'capitalize',
          fontWeight: 600,
          fontSize: 11,
          height: 22,
          color: state?.tone || statusTokens.neutral,
          bgcolor: state?.bg || statusTokens.neutralBg,
        }}
      />
    );
  },
};

const ROLE_COLUMN = {
  key: 'role',
  label: 'Role',
  render: (row) => <RoleChip role={row.role} />,
};

// Jobs per contractor, from AdminDashboardService._jobStatsFor.
const JOBS_COLUMN = {
  key: 'jobs',
  label: 'Jobs',
  render: (row) => (
    <AssignmentCount
      stats={row.jobStats}
      sourceLabels={{ projects: 'project', subcontracts: 'sub-contract' }}
    />
  ),
};

export const APPLICANT_GROUPS = {
  contractors: {
    role: 'contractor',
    title: 'Contractors',
    noun: 'contractor',
    basePath: '/admin/home/contractors',
    leadColumns: () => [APPROVAL_COLUMN],
    extraColumns: () => [EXPERIENCE_COLUMN, JOBS_COLUMN],
  },
  verifiers: {
    role: 'verifier',
    title: 'Verifiers',
    noun: 'verifier',
    basePath: '/admin/home/verifiers',
    leadColumns: () => [APPROVAL_COLUMN],
    extraColumns: () => [VERIFIES_COLUMN, VERIFICATIONS_COLUMN],
  },
  applications: {
    // Every gated role in one review queue. Defaults to Pending because this
    // is the "needs a decision" view, not a roster.
    role: 'all',
    title: 'Applications',
    noun: 'application',
    basePath: '/admin/home/applications',
    defaultApproval: 'pending',
    showRoleFilter: true,
    leadColumns: () => [ROLE_COLUMN, APPROVAL_COLUMN],
    extraColumns: () => [],
    // Straight into the review screen for that applicant.
    trailingColumns: () => [VIEW_COLUMN],
    showToolbar: true,
  },
};

const AdminApplicantsPage = ({ group }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const config = APPLICANT_GROUPS[group];

  // Absent ?approval= means the group's default: 'all' for a roster, 'pending'
  // for the Applications queue.
  const rawApproval = searchParams.get('approval');
  const approvalStatus = APPROVAL_KEYS.includes(rawApproval)
    ? rawApproval
    : (rawApproval === 'all' ? 'all' : config?.defaultApproval || 'all');
  const state = APPROVAL_STATES[approvalStatus] || null;

  // Only the mixed queue offers a role filter; the per-role pages are fixed.
  const rawRole = searchParams.get('role');
  const roleFilter = config?.showRoleFilter && APPLICANT_ROLE_OPTIONS.includes(rawRole)
    ? rawRole
    : 'all';

  const columns = [
    { key: 'image', label: 'Image' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    ...(config?.leadColumns?.() || []),
    { key: 'phone', label: 'Phone' },
    { key: 'location', label: 'Location' },
    ...(config?.extraColumns?.(approvalStatus) || []),
    { key: 'documents', label: 'Documents' },
    { key: 'applied', label: 'Applied' },
    ...(config?.trailingColumns?.() || []),
  ].filter(Boolean);

  const parsedPage = parseInt(searchParams.get('page'), 10);
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage - 1 : 0;

  const parsedLimit = parseInt(searchParams.get('limit'), 10);
  const rowsPerPage = PAGE_SIZES.includes(parsedLimit) ? parsedLimit : DEFAULT_PAGE_SIZE;

  const urlSearch = searchParams.get('search') || '';

  const writeParams = (updates, { replace = false } = {}) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value == null) params.delete(key);
      else params.set(key, String(value));
    });
    setSearchParams(params, { replace });
  };

  const setPagination = (nextPage, nextLimit = rowsPerPage, { replace = false } = {}) =>
    writeParams(
      {
        page: nextPage > 0 ? nextPage + 1 : '',
        limit: nextLimit !== DEFAULT_PAGE_SIZE ? nextLimit : '',
      },
      { replace }
    );

  /**
   * `isCancelled` guards against out-of-order responses: a slower earlier
   * request must not overwrite a newer one, or the table renders rows for a
   * filter the user has already moved off. Supplied by the effect below;
   * user-initiated calls (Retry, refresh) pass nothing and always apply.
   */
  const load = useCallback(async ({ isCancelled = () => false } = {}) => {
    if (!config) return;
    setLoading(true);
    try {
      const res = await adminApplicationsService.list({
        status: approvalStatus,
        role: config.showRoleFilter ? roleFilter : config.role,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(urlSearch ? { search: urlSearch } : {}),
      });
      if (isCancelled()) return;
      setRows(res?.applications || []);
      setTotal(res?.pagination?.total ?? 0);
      setError(null);
    } catch (err) {
      if (isCancelled()) return;
      setError(
        err?.response?.data?.error || err?.message || `Could not load ${config.noun}s.`
      );
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [approvalStatus, roleFilter, config, page, rowsPerPage, urlSearch]);

  useEffect(() => {
    let cancelled = false;
    load({ isCancelled: () => cancelled });
    return () => { cancelled = true; };
  }, [load]);

  const [searchInput, setSearchInput] = useState(urlSearch);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== urlSearch) {
        writeParams({ search: searchInput.trim(), page: '' }, { replace: true });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, urlSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSearchInput((current) => (current.trim() === urlSearch ? current : urlSearch));
  }, [urlSearch]);

  // Moving between states or groups resets the box along with the results.
  useEffect(() => {
    setSearchInput(searchParams.get('search') || '');
  }, [approvalStatus, roleFilter, group]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading || total === 0 || page === 0) return;
    const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
    if (page > lastPage) setPagination(lastPage, rowsPerPage, { replace: true });
  }, [loading, total, page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!config) return <Navigate to="/admin/home" replace />;

  const renderCell = (column, row) => {
    switch (column.key) {
      case 'image':
        return (
          <Avatar
            src={row.profileImageUrl || undefined}
            alt={fullNameOf(row) || row.email}
            sx={{
              width: 36,
              height: 36,
              fontSize: 13,
              fontWeight: 600,
              bgcolor: `${brand.primary}1a`,
              color: brand.primary,
            }}
          >
            {initialsOf(row)}
          </Avatar>
        );
      case 'name':
        return (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() =>
              navigate(`/admin/home/users/${row.id}`, {
                state: { listSearch: location.search, listPath: location.pathname },
              })
            }
            sx={{
              color: text.primary,
              fontWeight: 500,
              fontSize: 'inherit',
              fontFamily: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
              '&:hover': { color: brand.primary },
            }}
          >
            {fullNameOf(row) || row.email}
          </Link>
        );
      case 'email':
        return row.email;
      case 'phone':
        return row.phoneNumber || EMPTY;
      case 'location':
        return locationOf(row);
      case 'documents': {
        const docs = row.documentSummary;
        if (row.documentsWaived) {
          return (
            <Chip
              size="small"
              label="Waived"
              sx={{
                fontSize: 11,
                height: 22,
                color: statusTokens.neutral,
                bgcolor: statusTokens.neutralBg,
              }}
            />
          );
        }
        // computeDocumentChecklist returns requiredCount / satisfiedCount /
        // complete (backend src/config/approval.js).
        if (!docs || !docs.requiredCount) return EMPTY;
        return (
          <Chip
            size="small"
            label={`${docs.satisfiedCount ?? 0}/${docs.requiredCount}`}
            sx={{
              fontSize: 11,
              height: 22,
              fontWeight: 600,
              color: docs.complete ? statusTokens.successDark : statusTokens.warningDark,
              bgcolor: docs.complete ? statusTokens.successBg : statusTokens.warningBg,
            }}
          />
        );
      }
      case 'applied':
        return formatDate(row.createdAt);
      case 'view':
        return (
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
            onClick={() =>
              navigate(`/admin/home/applications/${row.id}`, {
                state: { listSearch: location.search, listPath: location.pathname },
              })
            }
            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            View
          </Button>
        );
      default:
        return column.render ? column.render(row) : EMPTY;
    }
  };

  const cellSx = (key) => {
    if (key === 'image') return { width: 64 };
    if (key === 'view') return { width: 1, textAlign: 'right', whiteSpace: 'nowrap' };
    if (key === 'name') return {};
    if (['phone', 'applied', 'experience', 'documents', 'verifications', 'jobs', 'approval'].includes(key)) {
      return { color: text.secondary, whiteSpace: 'nowrap' };
    }
    return { color: text.secondary };
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.25 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary }}>
          {config.title}
        </Typography>
        {state && (
          <Chip
            size="small"
            label={state.label}
            sx={{ fontWeight: 600, fontSize: 12, color: state.tone, bgcolor: state.bg }}
          />
        )}
      </Box>
      <Typography variant="body2" sx={{ color: text.secondary, mb: 2.5 }}>
        {loading
          ? 'Loading…'
          : `${total.toLocaleString()} ${config.noun}${total === 1 ? '' : 's'}${
              urlSearch ? ' matching' : ''
            }`}
      </Typography>

      {config.showToolbar && (
        <ApplicationsToolbar roleFilter={roleFilter} onChanged={load} />
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2.5 }}>
        <TextField
          size="small"
          placeholder="Search by name, email or phone"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ flex: 1, minWidth: { md: 280 }, bgcolor: bg.paper }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: text.tertiary }} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')} aria-label="Clear search">
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <TextField
          select
          size="small"
          label="Approval"
          value={approvalStatus}
          onChange={(e) =>
            writeParams({
              approval:
                e.target.value === 'all' && !config.defaultApproval ? '' : e.target.value,
              page: '',
            })
          }
          sx={{ minWidth: 170, bgcolor: bg.paper }}
        >
          <MenuItem value="all">All</MenuItem>
          {APPROVAL_KEYS.map((key) => (
            <MenuItem key={key} value={key}>
              {APPROVAL_STATES[key].label}
            </MenuItem>
          ))}
        </TextField>

        {config.showRoleFilter && (
          <TextField
            select
            size="small"
            label="Role"
            value={roleFilter}
            onChange={(e) =>
              writeParams({ role: e.target.value === 'all' ? '' : e.target.value, page: '' })
            }
            sx={{ minWidth: 180, bgcolor: bg.paper }}
          >
            <MenuItem value="all">All roles</MenuItem>
            {APPLICANT_ROLE_OPTIONS.map((r) => (
              <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>
                {r.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2.5, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={panelSx}>
        <TableContainer>
          <Table size="medium" sx={tableSx}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.key}>{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {columns.map((column) => (
                      <TableCell key={column.key}>
                        <Skeleton height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: text.tertiary }}>
                      {urlSearch
                        ? `No ${config.noun}s match this search.`
                        : state
                          ? `No ${state.label.toLowerCase()} ${config.noun}s.`
                          : `No ${config.noun}s yet.`}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} sx={tableRowSx}>
                    {columns.map((column) => (
                      <TableCell key={column.key} sx={cellSx(column.key)}>
                        {renderCell(column, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => setPagination(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setPagination(0, parseInt(e.target.value, 10))}
          rowsPerPageOptions={PAGE_SIZES}
          sx={{ borderTop: `1px solid ${border.light}` }}
        />
      </Paper>
    </Box>
  );
};

export default AdminApplicantsPage;
